<template>
  <div
    v-if="!disabled || (filters.length && disabled)"
    ref="element"
    class="tw:flex tw:flex-col tw:gap-2"
  >
    <div class="tw:flex tw:items-center tw:justify-between tw:gap-2">
      <div class="tw:text-xs tw:font-bold tw:text-denim-700">
        <i18n-t
          :keypath="titleKeypath"
          scope="global"
        >
          <template #title>
            <span class="tw:uppercase">{{ title }}</span>
          </template>
          <template #subtitle>
            <span>{{ subTitle }}</span>
          </template>
        </i18n-t>
      </div>

      <SuggestedKeywordsAIControls
        v-if="must"
        :ai-feature-configuration-id="aiFeatureConfigurationId"
        :is-ai-button-clearable="isAiButtonClearable"
        :is-ai-button-disabled="isAiButtonDisabled"
        :is-container-open="isContainerOpen"
        :keyword-suggestions="keywordSuggestions"
        :source="feedbackSource"
        :tags="filters"
        @regenerate-suggestions="emit('regenerateSuggestions')"
        @toggle-ai-feature="emit('toggleAiFeature')"
      />
    </div>

    <SuggestedKeywordsAIContainer
      :filter-open="filters.length > 0"
      :input-height="elementHeight"
      is-advance-keyword
      :is-container-open="isContainerOpen"
      :loading="loadingSuggestions"
    >
      <template #input>
        <div
          ref="elementRef"
          class="tw:rounded"
          :class="{
            'tw:border tw:border-denim-200 tw:focus-within:border-[var(--s-input-focus-border)]':
              !isContainerOpen,
          }"
        >
          <div
            class="tw:flex tw:flex-col tw:rounded tw:border-denim-200 tw:bg-white tw:px-2 tw:py-1 tw:focus-within:border-[var(--s-input-focus-border)] tw:hover:border-denim-400"
            :class="{
              'tw:p-2': disabled,
              'tw:px-3': !disabled,
              'tw:py-2': !disabled,
              'tw:mx-[10px] tw:my-[10px] tw:rounded-3xl tw:border':
                isContainerOpen,
            }"
          >
            <div
              v-if="filters.length"
              ref="tags"
              class="tw:flex tw:grow tw:gap-2 tw:overflow-auto tw:pb-2"
              data-testid="tags"
            >
              <SuggestedKeywordsBooleanTag
                v-for="(filter, filterIndex) in filters"
                :key="filterIndex"
                :disabled="disabled"
                :index="filterIndex"
                :is-social-stream="isSocialStream"
                :model-value="filter"
                :must-not="mustNot"
                :pending="isTagPending(filterIndex)"
                :should="should"
                @remove="removeFilter"
                @update:model-value="updateFilter($event, filterIndex)"
              />
            </div>

            <template v-if="!disabled">
              <div class="tw:flex tw:items-center tw:gap-2">
                <SuggestedKeywordsBooleanSelector
                  v-if="hasFocus"
                  ref="selector"
                  data-testid="keyword-selector"
                  :is-social-stream="isSocialStream"
                  :model-value="currentFilter"
                  @cancel="reset"
                  @update:model-value="applyFilter"
                />
                <div class="tw:flex tw:flex-1 tw:items-center tw:gap-2">
                  <div
                    v-if="currentFilter.type"
                    class="tw:text-denim-700"
                  >
                    {{ filterLabel }}
                  </div>
                  <div class="tw:flex-1">
                    <InputText
                      ref="keywordInput"
                      v-model="keyword"
                      borderless
                      data-testid="keyword-input"
                      dense
                      new-design
                      :placeholder="
                        getLocaleText(
                          'stream_settings_simple_boolean_section.keyword_placeholder'
                        )
                      "
                      @focus="hasFocus = true"
                      @keydown="onKeyword"
                    />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>

      <template #result>
        <SuggestedKeywordsAISuggestions
          :is-container-open="isContainerOpen"
          :is-loading="loadingSuggestions"
          :is-suggestion-visible="isAiButtonClearable"
          :keyword-api-error="keywordApiError"
          :keyword-suggestions="keywordSuggestions"
          :safety-error="safetyError"
          :tags="filters"
          @add-suggestion="addSuggestion"
          @remove-suggestion="removeSuggestion"
        />
      </template>
    </SuggestedKeywordsAIContainer>

    <div
      v-if="error"
      class="field-error"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { InputText } from "shared/components/base";
import { useClickOutside } from "shared/composables/useClickOutside";
import useElementHeight from "shared/composables/useElementHeight";
import type { SimpleBooleanFilter } from "shared/resources/Stream";
import { SimpleBooleanFilterType } from "shared/resources/Stream";
import type { Nullable } from "shared/types";

import SuggestedKeywordsAIContainer from "./SuggestedKeywordsAIContainer.vue";
import SuggestedKeywordsAIControls from "./SuggestedKeywordsAIControls.vue";
import SuggestedKeywordsAISuggestions from "./SuggestedKeywordsAISuggestions.vue";
import SuggestedKeywordsBooleanSelector from "./SuggestedKeywordsBooleanSelector.vue";
import SuggestedKeywordsBooleanTag from "./SuggestedKeywordsBooleanTag.vue";

interface SuggestedKeywordsBooleanSectionProps {
  aiFeatureConfigurationId?: number;
  disabled?: boolean;
  must?: boolean;
  mustNot?: boolean;
  should?: boolean;
  error?: boolean;
  errorMessage?: string;
  isSocialStream?: boolean;
  isContainerOpen?: boolean;
  loadingSuggestions?: boolean;
  isAiButtonClearable?: boolean;
  isAiButtonDisabled?: boolean;
  keywordSuggestions?: string[];
  keywordApiError?: boolean;
  safetyError?: boolean;
}

const filters = defineModel<SimpleBooleanFilter[]>({ default: () => [] });

const props = defineProps<SuggestedKeywordsBooleanSectionProps>();

const element = useTemplateRef("element");

const { elementRef, elementHeight } = useElementHeight();

const selector = ref();
const currentFilter = ref<SimpleBooleanFilter>({});
const keywordInput = ref();
const keyword = ref("");
const tags = ref<HTMLDivElement>();
const markLastTag = ref(false);
const hasFocus = ref(false);
let markLastTagTimer: Nullable<ReturnType<typeof setTimeout>> = null;

const emit = defineEmits<{
  toggleAiFeature: [];
  regenerateSuggestions: [];
}>();

const feedbackSource = computed(
  () =>
    `${
      props.isSocialStream ? "Social" : "Monitoring"
    } - simple boolean filter - ${title.value} ${subTitle.value}`
);

useClickOutside(
  element,
  () => {
    hasFocus.value = false;
  },
  {
    active: hasFocus,
    capture: true,
    events: ["mousedown"],
  }
);

const titleKeypath = computed(() => {
  if (props.mustNot) {
    return "stream_settings_simple_boolean_section.must_not_full_title";
  }

  if (props.should) {
    return "stream_settings_simple_boolean_section.should_full_title";
  }

  return "stream_settings_simple_boolean_section.must_full_title";
});

const title = computed(() => {
  if (props.mustNot) {
    return getLocaleText(
      "stream_settings_simple_boolean_section.must_not_title"
    );
  }

  if (props.should) {
    return getLocaleText("stream_settings_simple_boolean_section.should_title");
  }

  return getLocaleText("stream_settings_simple_boolean_section.must_title");
});

const subTitle = computed(() => {
  if (props.mustNot) {
    return getLocaleText(
      "stream_settings_simple_boolean_section.must_not_subtitle"
    );
  }

  if (props.should) {
    return getLocaleText(
      "stream_settings_simple_boolean_section.should_subtitle"
    );
  }

  return getLocaleText("stream_settings_simple_boolean_section.must_subtitle");
});

const filterLabel = computed(() => {
  if (!currentFilter.value.type) {
    return "";
  }

  if (currentFilter.value.type === SimpleBooleanFilterType.CASE_SENSITIVE) {
    return getLocaleText(
      "stream_settings_simple_boolean_section.case_sensitive_label"
    );
  }

  if (currentFilter.value.type === SimpleBooleanFilterType.FIRST) {
    return getLocaleText("stream_settings_simple_boolean_section.first_label", {
      value: currentFilter.value.value,
    });
  }

  if (currentFilter.value.type === SimpleBooleanFilterType.AT_LEAST) {
    return getLocaleText(
      "stream_settings_simple_boolean_section.at_least_label",
      {
        value: currentFilter.value.value,
      }
    );
  }

  if (
    currentFilter.value.type === SimpleBooleanFilterType.MEDIUM &&
    Array.isArray(currentFilter.value.value)
  ) {
    return getLocaleText(
      "stream_settings_simple_boolean_section.medium_label",
      { medium: currentFilter.value.value.join(" OR ") }
    );
  }

  if (
    currentFilter.value.type === SimpleBooleanFilterType.PLATFORM &&
    Array.isArray(currentFilter.value.value)
  ) {
    return getLocaleText(
      "stream_settings_simple_boolean_section.platform_label",
      {
        platform: currentFilter.value.value.join(" OR "),
      }
    );
  }

  return getLocaleText(
    `stream_settings_simple_boolean_section.${currentFilter.value.type}_label`
  );
});

function reset() {
  currentFilter.value = {};
  keyword.value = "";
}

function applyFilter(filter: SimpleBooleanFilter) {
  currentFilter.value = filter;
  keywordInput.value!.focus();
}

function addFilter() {
  if (!keyword.value) {
    return;
  }

  const filter = {
    ...currentFilter.value,
    keyword: keyword.value,
  };

  filters.value = filters.value.concat(filter);

  scrollToEndOfTags();

  reset();
}

async function scrollToEndOfTags() {
  await nextTick();

  if (tags.value) {
    tags.value.scrollLeft = tags.value.scrollWidth;
  }
}

function removeFilter(index: number) {
  filters.value = filters.value.toSpliced(index, 1);
}

function startMarkLastTagTimer() {
  markLastTagTimer = setTimeout(() => {
    markLastTag.value = false;
  }, 3000);
}

function stopMarkLastTagTimer() {
  if (markLastTagTimer) clearTimeout(markLastTagTimer);
  markLastTagTimer = null;
}

function onKeyword(event: KeyboardEvent) {
  event.stopPropagation();

  if (selector.value) {
    selector.value.close();
  }

  if (event.key === "Backspace" && !keyword.value) {
    if (currentFilter.value.type) {
      reset();

      return;
    }

    if (!markLastTag.value) {
      markLastTag.value = true;
      startMarkLastTagTimer();

      return;
    }

    if (markLastTag.value) {
      filters.value = filters.value.toSpliced(-1, 1);
      markLastTag.value = false;
      stopMarkLastTagTimer();

      return;
    }
  }

  markLastTag.value = false;
  stopMarkLastTagTimer();

  if (event.key === "Enter") {
    event.preventDefault();
    addFilter();
  }
}

function isTagPending(index: number): boolean {
  return index === filters.value.length - 1 && markLastTag.value;
}

function updateFilter(newFilter: SimpleBooleanFilter, filterIndex: number) {
  filters.value = filters.value.toSpliced(filterIndex, 1, newFilter);
}

function removeSuggestion(index: number) {
  filters.value = filters.value.filter(
    (filter) => filter.suggestionIndex !== index
  );
}

function addSuggestion(value: string, index: number) {
  filters.value = filters.value.concat({
    suggestionIndex: index,
    isSuggestion: true,
    keyword: value,
  });

  scrollToEndOfTags();
}

onBeforeUnmount(() => {
  stopMarkLastTagTimer();
});
</script>
